import { useMemo, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { CATEGORIES, CATEGORY_LABEL, formatQty, unitWord, useIngredients, useStockMutation, type StockCategory } from '../api/stock';
import { formatDate } from '../format';
import { Button, ErrorNote, Field, Loading, useReport, useToast } from '../ui/kit';

interface CountResult {
  changed: { id: string; name: string; before: number; after: number }[];
  same: number;
  firstCounts: number;
}

const whole = (s: string): number | null => (/^\d{1,9}$/.test(s.trim()) ? Number(s.trim()) : null);

/**
 * The monthly count: print a sheet by place or kind, walk round with a pen, then type every figure in on one screen.
 * By default the sheet does not show what the system expects, so the count is honest; it can be shown to check against.
 */
export function CountSheet() {
  const { me } = useAuth();
  const toast = useToast();
  const report = useReport();
  const q = useIngredients();
  const today = me?.property.today ?? '';
  const monthLabel = today ? new Date(`${today.slice(0, 7)}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '';
  const [place, setPlace] = useState('');
  const [kind, setKind] = useState<StockCategory | ''>('');
  const [showSystem, setShowSystem] = useState(false);
  const [entered, setEntered] = useState<Record<string, string>>({});
  const [note, setNote] = useState(`Monthly count ${monthLabel}`);
  const [result, setResult] = useState<CountResult | null>(null);
  const save = useStockMutation((body: { note: string; counts: { ingredientId: string; counted: number }[] }) => api<CountResult>('/stock/count', { method: 'POST', body }));

  const active = useMemo(() => (q.data ?? []).filter((i) => i.active), [q.data]);
  const places = useMemo(() => [...new Set(active.map((i) => i.location).filter((x): x is string => !!x))].sort(), [active]);
  const rows = useMemo(
    () => active.filter((i) => (place === '' ? true : place === '__none' ? !i.location : i.location === place) && (kind === '' || i.category === kind)).sort((a, b) => (a.location ?? '~').localeCompare(b.location ?? '~') || a.name.localeCompare(b.name)),
    [active, place, kind],
  );

  if (q.isLoading) return <Loading rows={4} />;
  if (q.error) return <ErrorNote error={q.error} retry={() => void q.refetch()} />;

  const lines = Object.entries(entered).filter(([, v]) => v.trim() !== '');
  const bad = lines.filter(([, v]) => whole(v) === null);
  const counts = lines.filter(([, v]) => whole(v) !== null).map(([ingredientId, v]) => ({ ingredientId, counted: whole(v) as number }));
  const differs = counts.filter((c) => {
    const i = active.find((x) => x.id === c.ingredientId);
    return i && (c.counted !== i.onHand || !i.counted);
  }).length;
  const title = `${me?.property.name ?? ''}: stock count${place && place !== '__none' ? `, ${place}` : ''}${kind ? `, ${CATEGORY_LABEL[kind]}` : ''}, ${today ? formatDate(today, true) : ''}`;

  function submit() {
    save.mutate(
      { note: note.trim(), counts },
      {
        onError: report,
        onSuccess: (r) => {
          setResult(r);
          setEntered({});
          toast.ok(`Saved ${counts.length} count${counts.length === 1 ? '' : 's'}`);
        },
      },
    );
  }

  return (
    <div className="stack">
      <div className="row no-print" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field id="cs-place" label="Place">
          <select id="cs-place" className="input" value={place} onChange={(e) => setPlace(e.target.value)}>
            <option value="">Everywhere</option>
            {places.map((p) => <option key={p} value={p}>{p}</option>)}
            <option value="__none">No place set</option>
          </select>
        </Field>
        <Field id="cs-kind" label="Kind">
          <select id="cs-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as StockCategory | '')}>
            <option value="">All kinds</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
          </select>
        </Field>
        <Button onClick={() => window.print()} disabled={rows.length === 0}>Print the sheet</Button>
      </div>
      <label className="row small no-print">
        <input type="checkbox" checked={showSystem} onChange={(e) => setShowSystem(e.target.checked)} /> Show what the system expects (leave off for an honest count)
      </label>

      {result ? (
        <section className="card stack no-print" aria-label="Count saved">
          <div className="eyebrow">Count saved</div>
          <p>{result.changed.length === 0 ? 'Every count matched the system.' : `${result.changed.length} item${result.changed.length === 1 ? '' : 's'} did not match and ${result.changed.length === 1 ? 'was' : 'were'} corrected:`}{result.same > 0 ? ` ${result.same} matched.` : ''}{result.firstCounts > 0 ? ` ${result.firstCounts} counted for the first time.` : ''}</p>
          {result.changed.length > 0 ? (
            <ul className="list">
              {result.changed.map((c) => {
                const i = active.find((x) => x.id === c.id);
                const w = i ? unitWord(i.unit, i.unitName) : '';
                return <li key={c.id} className="item small">{c.name}: system said {c.before.toLocaleString('en-GH')}, counted {c.after.toLocaleString('en-GH')} {w} ({c.after - c.before > 0 ? '+' : '−'}{Math.abs(c.after - c.before).toLocaleString('en-GH')})</li>;
              })}
            </ul>
          ) : null}
          <div><Button size="small" onClick={() => setResult(null)}>Done</Button></div>
        </section>
      ) : null}

      <p className="small muted print-only-title">{title}. Counted by: ______________________</p>
      {rows.length === 0 ? (
        <p className="muted">Nothing to count here. Add items under Items, or change the place or kind.</p>
      ) : (
        <div className="table-scroll">
          <table className="ledger fin" aria-label="Count sheet">
            <thead>
              <tr>
                <th>Item</th>
                <th>Kept in</th>
                {showSystem ? <th className="r">System</th> : null}
                <th className="r">Counted</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => {
                const v = entered[i.id] ?? '';
                const invalid = v.trim() !== '' && whole(v) === null;
                return (
                  <tr key={i.id}>
                    <td>{i.name}<span className="small muted"> · {CATEGORY_LABEL[i.category]}</span></td>
                    <td>{i.location ?? ''}</td>
                    {showSystem ? <td className="r num">{i.counted ? formatQty(i.onHand, i.unit, i.unitName) : 'not counted'}</td> : null}
                    <td className="r">
                      <input
                        className="input count-input"
                        inputMode="numeric"
                        aria-label={`Counted: ${i.name} (${unitWord(i.unit, i.unitName)})`}
                        aria-invalid={invalid || undefined}
                        placeholder={unitWord(i.unit, i.unitName)}
                        value={v}
                        onChange={(e) => setEntered({ ...entered, [i.id]: e.target.value })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="card stack no-print">
        <Field id="cs-note" label="Reason for the corrections" hint="Written beside every difference in the history">
          <input id="cs-note" className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {bad.length > 0 ? <div className="banner bad">{bad.length} figure{bad.length === 1 ? ' is' : 's are'} not a whole number. Fix {bad.length === 1 ? 'it' : 'them'} before saving.</div> : null}
        <p className="small muted">{counts.length === 0 ? 'Type what you counted in the boxes above. Leave an item empty to skip it.' : `${counts.length} item${counts.length === 1 ? '' : 's'} entered, ${differs} to be corrected.`} If stock moved while you were counting, count again for those items.</p>
        <Button variant="primary" disabled={counts.length === 0 || bad.length > 0 || note.trim().length < 3} loading={save.isPending} onClick={submit}>
          Save the counts
        </Button>
      </div>
    </div>
  );
}
